"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _InputLabel = _interopRequireDefault(require("../InputLabel"));
var _Button = require("../Button");
var _Notification = _interopRequireDefault(require("../Notification"));
var _Spacer = _interopRequireDefault(require("../Spacer"));
var _StackView = _interopRequireDefault(require("../StackView"));
var _NotificationContent = _interopRequireDefault(require("./NotificationContent"));
var _dictionary = _interopRequireDefault(require("./dictionary"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const getHintFromFileTypes = fileTypes => {
  const acceptedFileTypes = fileTypes.map(type => type.toUpperCase());
  return acceptedFileTypes.reduce((acc, curr, index) => {
    if (index === 0) {
      return `${curr}`;
    }
    return `${acc}, ${curr}`;
  }, '');
};
const stringifyFileTypesList = (fileTypes, getCopy) => {
  const acceptedFileTypes = fileTypes.map(type => `.${type.toUpperCase()}`);
  return acceptedFileTypes.reduce((acc, curr, index) => {
    if (index === 0) {
      return curr;
    }
    if (index === acceptedFileTypes.length - 1) {
      return `${acc} ${getCopy('or')} ${curr}`;
    }
    return `${acc}, ${curr}`;
  }, '');
};
const selectComponentTokens = (tokens, componentIdentifier) => {
  return Object.entries(tokens).reduce((acc, _ref) => {
    let [key, value] = _ref;
    if (key.startsWith(componentIdentifier) && value !== null) {
      const newKey = key.replace(componentIdentifier, '').replace('Text', '').charAt(0).toLowerCase() + key.replace(componentIdentifier, '').replace('Text', '').slice(1);
      acc[newKey] = value;
    }
    return acc;
  }, {});
};

/**
 * FileUpload component for uploading files.
 *
 * @component
 * @example
 * // Usage:
 * <FileUpload
 *   tokens={tokens}
 *   variant={variant}
 *   copy={copy}
 *   fileTypes={fileTypes}
 *   allowMultipleFiles={allowMultipleFiles}
 *   maxFileSize={maxFileSize}
 *   maxFilesCount={maxFilesCount}
 *   onUpload={onUpload}
 *   onDelete={onDelete}
 *   documentPicker={documentPicker}
 *   {...rest}
 * />
 *
 * @param {Object} props - The component props.
 * @param {Object} props.tokens - The tokens for styling the component.
 * @param {string} props.variant - The variant of the component.
 * @param {string} props.copy - The copy for the component.
 * @param {string[]} props.fileTypes - The allowed file types for upload.
 * @param {boolean} props.allowMultipleFiles - Whether multiple files can be uploaded.
 * @param {number} props.maxFileSize - The maximum file size in megabytes.
 * @param {number} props.maxFilesCount - The maximum number of files that can be uploaded.
 * @param {number} props.minFilesCount - The minimum number of files that can be uploaded.
 * @param {number} props.minFileSize - The minimum file size in megabytes.
 * @param {Function} props.onUpload - The callback function for file upload.
 * @param {Function} props.onDelete - The callback function for file deletion.
 * @param {Object} props.documentPicker - The document picker object.
 * @param {any} props.rest - Additional props to be spread to the component.
 * @returns {JSX.Element} The rendered FileUpload component.
 */
const FileUpload = /*#__PURE__*/_react.default.forwardRef((_ref2, ref) => {
  let {
    tokens,
    variant,
    copy = 'en',
    fileTypes,
    allowMultipleFiles = false,
    maxFileSize,
    maxFilesCount = 1,
    minFilesCount = 1,
    minFileSize = 0,
    onUpload,
    onDelete,
    documentPicker,
    dictionary = _dictionary.default,
    ...rest
  } = _ref2;
  if (minFilesCount <= 0) {
    throw new Error('minFilesCount should be greater than 0');
  }
  const themeTokens = (0, _ThemeProvider.useThemeTokens)('FileUpload', tokens, variant);
  const getCopy = (0, _utils.useCopy)({
    dictionary,
    copy
  });
  const inputFileRef = _react.default.useRef(null);
  const [filesStatus, setFilesStatus] = _react.default.useState([]);
  const [renderTrigger, setRenderTrigger] = _react.default.useState(0);
  _react.default.useEffect(() => {
    setRenderTrigger(prev => prev + 1);
  }, [filesStatus]);
  const areFileTypesExtensionsValid = files => {
    const invalidFiles = files.filter(file => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      return !fileTypes.includes(fileExtension);
    });
    if (invalidFiles.length > 0) {
      setFilesStatus([{
        description: getCopy('wrongFileType').replace('%{fileType}', stringifyFileTypesList(fileTypes, getCopy)),
        style: 'error'
      }]);
      return false;
    }
    return true;
  };
  const areFileSizesWithinLimit = files => {
    const invalidFiles = files.filter(file => {
      const fileSize = file.size;
      return fileSize < (0, _utils.convertFromMegaByteToByte)(minFileSize) || fileSize > (0, _utils.convertFromMegaByteToByte)(maxFileSize);
    });
    if (invalidFiles.length > 0) {
      const invalidFile = invalidFiles[0];
      const fileSize = invalidFile.size;
      if (fileSize < (0, _utils.convertFromMegaByteToByte)(minFileSize)) {
        setFilesStatus([{
          description: getCopy('fileTooSmall').replace('%{size}', minFileSize),
          style: 'error'
        }]);
      } else {
        setFilesStatus([{
          description: getCopy('fileTooBig').replace('%{size}', maxFileSize),
          style: 'error'
        }]);
      }
      return false;
    }
    return true;
  };
  const areFilesCountWithinLimit = files => {
    if (maxFilesCount && files.length > maxFilesCount) {
      setFilesStatus([{
        description: getCopy('tooManyFiles').replace('%{maxFilesCount}', maxFilesCount),
        style: 'error'
      }]);
      return false;
    }
    if (minFilesCount && files.length < minFilesCount) {
      setFilesStatus([{
        description: getCopy('fewFiles').replace('%{minFilesCount}', minFilesCount),
        style: 'error'
      }]);
      return false;
    }
    return true;
  };
  const handleFilesUpload = async files => {
    try {
      const results = await onUpload(files);
      const currentFilesStatus = files.map((file, index) => {
        if (results[index].error) {
          return {
            description: /*#__PURE__*/(0, _jsxRuntime.jsx)(_NotificationContent.default, {
              file: file,
              color: selectComponentTokens(themeTokens, 'notification').color,
              children: ` ${getCopy('uploadError')} ${results[index].error}.`
            }),
            style: 'error'
          };
        }
        return {
          description: /*#__PURE__*/(0, _jsxRuntime.jsx)(_NotificationContent.default, {
            file: file,
            color: selectComponentTokens(themeTokens, 'notification').color,
            children: ` ${getCopy('uploadSuccess')}`
          }),
          style: 'success',
          file,
          onDismiss: () => handleFileDeletion(file)
        };
      });
      setFilesStatus(currentFilesStatus);
    } catch (error) {
      setFilesStatus([{
        description: allowMultipleFiles ? getCopy('problemUploadingMultipleFiles') : getCopy('problemUploading'),
        style: 'error'
      }]);
    }
  };
  const handleFileDeletion = async file => {
    try {
      const result = await onDelete(file);
      setFilesStatus(prevStatus => {
        const filteredFilesStatus = prevStatus.filter(f => f.file && f.file.name !== file.name);
        return result.error ? [...filteredFilesStatus, {
          description: /*#__PURE__*/(0, _jsxRuntime.jsx)(_NotificationContent.default, {
            file: file,
            color: selectComponentTokens(themeTokens, 'notification').color,
            children: ` ${getCopy('uploadSuccess')}`
          }),
          style: 'success',
          file,
          onDismiss: () => handleFileDeletion(file)
        }, {
          description: /*#__PURE__*/(0, _jsxRuntime.jsx)(_NotificationContent.default, {
            file: file,
            color: selectComponentTokens(themeTokens, 'notification').color,
            children: ` ${getCopy('deleteProblem')} ${result.error}.`
          }),
          style: 'error',
          file
        }] : filteredFilesStatus;
      });
    } catch (error) {
      setFilesStatus([{
        description: /*#__PURE__*/(0, _jsxRuntime.jsx)(_NotificationContent.default, {
          file: file,
          color: selectComponentTokens(themeTokens, 'notification').color,
          children: ` ${getCopy('problemDeleting')}`
        }),
        style: 'error'
      }]);
    }
  };
  const handleOnChange = async event => {
    const targetFiles = event.target.files;
    const files = Array.from(targetFiles);
    if (files.length === 0) return;
    if (fileTypes && !areFileTypesExtensionsValid(files)) return;
    if (maxFileSize && !areFileSizesWithinLimit(files)) return;
    if ((maxFilesCount || minFilesCount) && !areFilesCountWithinLimit(files)) return;
    await handleFilesUpload(files);
    if (inputFileRef.current) inputFileRef.current.value = '';
  };
  const onFileUploadButtonClick = async () => {
    if (_Platform.default.OS === 'web') {
      inputFileRef.current.click();
    } else {
      try {
        const result = await documentPicker.getDocumentAsync({
          multiple: allowMultipleFiles
        });
        if (result) {
          if (result.canceled) return;
          const files = Array.isArray(result.assets) ? result.assets : [result.assets];
          const event = {
            target: {
              files
            }
          };
          await handleOnChange(event);
        }
      } catch (_) {
        setFilesStatus([{
          description: allowMultipleFiles ? getCopy('problemUploadingFiles') : getCopy('problemUploading'),
          style: 'error'
        }]);
      }
    }
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
    ref: ref,
    ...selectProps(rest),
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_InputLabel.default, {
      label: getCopy('label'),
      hint: fileTypes && getCopy('allowedFileTypes').replace('%{fileTypes}', getHintFromFileTypes(fileTypes)),
      hintPosition: "below"
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Spacer.default, {
      space: 4
    }), _Platform.default.OS === 'web' && /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
      type: "file",
      multiple: allowMultipleFiles,
      ref: inputFileRef,
      hidden: true,
      accept: fileTypes && fileTypes.map(type => `.${type}`).join(', '),
      onChange: handleOnChange
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Button.Button, {
      onClick: onFileUploadButtonClick,
      tokens: selectComponentTokens(themeTokens, 'button'),
      children: getCopy('buttonLabel')
    }), filesStatus.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Spacer.default, {
        space: 4
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_StackView.default, {
        space: 4,
        children: filesStatus.map((_ref3, index) => {
          let {
            description,
            style,
            onDismiss
          } = _ref3;
          return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Notification.default, {
            variant: {
              style
            },
            dismissible: !!onDismiss,
            onDismiss: onDismiss,
            tokens: selectComponentTokens(themeTokens, 'notification'),
            children: description
          }, `${index.toString()}-${renderTrigger}`);
        })
      })]
    })]
  });
});
FileUpload.displayName = 'FileUpload';
const dictionaryEntryShape = _propTypes.default.shape({
  label: _propTypes.default.string.isRequired,
  buttonLabel: _propTypes.default.string.isRequired,
  dismissButtonLabel: _propTypes.default.string.isRequired,
  wrongFileType: _propTypes.default.string.isRequired,
  allowedFileTypes: _propTypes.default.string.isRequired,
  fileTooBig: _propTypes.default.string.isRequired,
  fileIsEmpty: _propTypes.default.string.isRequired,
  problemUploading: _propTypes.default.string.isRequired,
  problemDeleting: _propTypes.default.string.isRequired,
  problemUploadingMultipleFiles: _propTypes.default.string.isRequired,
  only: _propTypes.default.string.isRequired,
  and: _propTypes.default.string.isRequired,
  or: _propTypes.default.string.isRequired,
  uploadSuccess: _propTypes.default.string.isRequired,
  uploadError: _propTypes.default.string.isRequired,
  deleteProblem: _propTypes.default.string.isRequired,
  tooManyFiles: _propTypes.default.string.isRequired,
  fileTooSmall: _propTypes.default.string.isRequired,
  fewFiles: _propTypes.default.string.isRequired
});
FileUpload.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _utils.getTokensPropType)('FileUpload'),
  variant: _utils.variantProp.propType,
  /**
   * To define the locale of the copy
   */
  copy: _utils.copyPropTypes,
  /**
   * The file types allowed for upload. Example: fileTypes={['pdf', 'docx']}.
   */
  fileTypes: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * Whether to allow multiple files to be uploaded.
   */
  allowMultipleFiles: _propTypes.default.bool,
  /**
   * The maximum file size allowed for upload in MB.
   */
  maxFileSize: _propTypes.default.number,
  /**
   * The maximum number of files allowed for upload.
   */
  maxFilesCount: _propTypes.default.number,
  /**
   * Callback function called when a file is uploaded.
   */
  onUpload: _propTypes.default.func,
  /**
   * Callback function called when a file is deleted
   */
  onDelete: _propTypes.default.func,
  /**
   * The document picker to use for mobile
   */
  documentPicker: _propTypes.default.object,
  /**
   * The minimum number of files allowed for upload.
   */
  minFilesCount: _propTypes.default.number,
  /**
   * The minimum file size allowed for upload in MB.
   */
  minFileSize: _propTypes.default.number,
  /**
   * Custom dictionary containing the labels to use for the steps
   */
  dictionary: _propTypes.default.shape({
    en: dictionaryEntryShape,
    fr: dictionaryEntryShape
  })
};
var _default = exports.default = FileUpload;